import { getAlgoliaClient, getAlgoliaConfig, isAlgoliaAvailable } from "@/lib/adapters";
import { algoliaConfig } from "@/lib/config/algolia-config";

import type { Patient } from "./patient-service";

// Algolia client configuration
export class AlgoliaSearchService {
  private client: any = null;
  private patientsIndex: any = null;
  private documentsIndex: any = null;
  private proceduresIndex: any = null;
  private isAvailable: boolean = false;

  constructor() {
    // Use centralized Algolia adapter
    this.isAvailable = isAlgoliaAvailable();

    if (this.isAvailable) {
      try {
        this.client = getAlgoliaClient();
        const config = getAlgoliaConfig();
        // In Algolia v5, there's no initIndex method - we just use the client directly
        this.patientsIndex = config.indices.patients;
        this.documentsIndex = config.indices.documents;
        this.proceduresIndex = config.indices.procedures;
      } catch (_error) {
        this.isAvailable = false;
      }
    }
  }

  // Patient Search Methods
  async searchPatients(
    query: string,
    options: {
      specialty?: string;
      status?: string;
      limit?: number;
      page?: number;
    } = {}
  ) {
    if (!this.isAvailable) {
      return this.mockSearchPatients(query, options);
    }

    try {
      const filters = [];
      if (options.specialty) filters.push(`specialty:"${options.specialty}"`);
      if (options.status) filters.push(`status:"${options.status}"`);

      const searchParams = {
        query,
        hitsPerPage: options.limit || 20,
        page: options.page || 0,
        filters: filters.join(" AND "),
        attributesToRetrieve: [
          "objectID",
          "name",
          "mrn",
          "age",
          "specialty",
          "status",
          "chiefComplaint",
          "lastVisit",
          "email",
          "phone",
        ],
        attributesToHighlight: ["name", "mrn", "chiefComplaint"],
        attributesToSnippet: algoliaConfig.snippetAttributes.patients,
        typoTolerance: true,
        minWordSizefor1Typo: algoliaConfig.typoTolerance.minWordSizefor1Typo,
        minWordSizefor2Typos: algoliaConfig.typoTolerance.minWordSizefor2Typos,
      };

      // In Algolia v5, we use the client directly with index name
      const results = await this.client.searchSingleIndex({
        indexName: this.patientsIndex,
        searchParams,
      });

      return {
        success: true,
        data: results.hits,
        total: results.nbHits,
        page: results.page,
        pages: results.nbPages,
        error: undefined,
      };
    } catch (_error: unknown) {
      return this.mockSearchPatients(query, options);
    }
  }

  async searchDocuments(
    query: string,
    options: {
      patientId?: string;
      documentType?: string;
      dateRange?: { start: string; end: string };
      limit?: number;
      page?: number;
    } = {}
  ) {
    if (!this.isAvailable) {
      return this.mockSearchDocuments(query, options);
    }

    try {
      const filters = [];
      if (options.patientId) filters.push(`patientId:"${options.patientId}"`);
      if (options.documentType) filters.push(`type:"${options.documentType}"`);
      if (options.dateRange) {
        filters.push(`timestamp:${options.dateRange.start} TO ${options.dateRange.end}`);
      }

      const results = await this.client.searchSingleIndex({
        indexName: this.documentsIndex,
        searchParams: {
          query,
          hitsPerPage: options.limit || 20,
          page: options.page || 0,
          filters: filters.join(" AND "),
          attributesToRetrieve: [
            "objectID",
            "title",
            "type",
            "patientId",
            "patientName",
            "timestamp",
            "summary",
            "provider",
          ],
          attributesToHighlight: ["title", "summary", "content"],
          attributesToSnippet: algoliaConfig.snippetAttributes.documents,
          typoTolerance: true,
          minWordSizefor1Typo: algoliaConfig.typoTolerance.minWordSizefor1Typo,
          minWordSizefor2Typos: algoliaConfig.typoTolerance.minWordSizefor2Typos,
        },
      });

      return {
        success: true,
        data: results.hits,
        total: results.nbHits,
        page: results.page,
        pages: results.nbPages,
        error: undefined,
      };
    } catch (_error: unknown) {
      return this.mockSearchDocuments(query, options);
    }
  }

  async searchProcedures(
    query: string,
    options: {
      specialty?: string;
      dateRange?: { start: string; end: string };
      limit?: number;
      page?: number;
    } = {}
  ) {
    if (!this.isAvailable) {
      return this.mockSearchProcedures(query, options);
    }

    try {
      const filters = [];
      if (options.specialty) filters.push(`specialty:"${options.specialty}"`);
      if (options.dateRange) {
        filters.push(`date:${options.dateRange.start} TO ${options.dateRange.end}`);
      }

      const results = await this.client.searchSingleIndex({
        indexName: this.proceduresIndex,
        searchParams: {
          query,
          hitsPerPage: options.limit || 20,
          page: options.page || 0,
          filters: filters.join(" AND "),
          attributesToRetrieve: [
            "objectID",
            "name",
            "cptCode",
            "specialty",
            "patientId",
            "patientName",
            "date",
            "provider",
            "status",
          ],
          attributesToHighlight: ["name", "description"],
          typoTolerance: true,
          minWordSizefor1Typo: algoliaConfig.typoTolerance.minWordSizefor1Typo,
          minWordSizefor2Typos: algoliaConfig.typoTolerance.minWordSizefor2Typos,
        },
      });

      return {
        success: true,
        data: results.hits,
        total: results.nbHits,
        page: results.page,
        pages: results.nbPages,
        error: undefined,
      };
    } catch (_error: unknown) {
      return this.mockSearchProcedures(query, options);
    }
  }

  // Advanced Search Methods
  async searchWithFacets(
    query: string,
    options: {
      facets?: string[];
      facetFilters?: string[][];
      limit?: number;
      page?: number;
    } = {}
  ) {
    if (!this.isAvailable) {
      return this.mockSearchPatients(query, options);
    }

    try {
      const searchParams = {
        query,
        hitsPerPage: options.limit || 20,
        page: options.page || 0,
        facets: options.facets || ["specialty", "status", "gender"],
        facetFilters: options.facetFilters || [],
        attributesToRetrieve: [
          "objectID",
          "name",
          "mrn",
          "age",
          "specialty",
          "status",
          "gender",
          "medications",
          "allergies",
          "problems",
        ],
        attributesToHighlight: ["name", "mrn", "medications", "allergies", "problems"],
        typoTolerance: true,
      };

      const results = await this.client.searchSingleIndex({
        indexName: this.patientsIndex,
        searchParams,
      });

      return {
        success: true,
        data: results.hits,
        total: results.nbHits,
        page: results.page,
        pages: results.nbPages,
        facets: results.facets,
      };
    } catch (_error: unknown) {
      return this.mockSearchPatients(query, options);
    }
  }

  // Multi-index search
  async searchMultipleIndices(
    query: string,
    options: {
      limit?: number;
    } = {}
  ) {
    if (!this.isAvailable) {
      // Return mock results from all indices
      const patients = await this.mockSearchPatients(query, { limit: options.limit || 10 });
      const documents = await this.mockSearchDocuments(query, { limit: options.limit || 10 });
      const procedures = await this.mockSearchProcedures(query, { limit: options.limit || 10 });

      return {
        success: true,
        results: [patients, documents, procedures],
      };
    }

    try {
      const results = await this.client.search({
        requests: [
          {
            indexName: this.patientsIndex,
            query,
            hitsPerPage: options.limit || 10,
          },
          {
            indexName: this.documentsIndex,
            query,
            hitsPerPage: options.limit || 10,
          },
          {
            indexName: this.proceduresIndex,
            query,
            hitsPerPage: options.limit || 10,
          },
        ],
      });

      return {
        success: true,
        results: results.results,
      };
    } catch (_error: unknown) {
      const patients = await this.mockSearchPatients(query, { limit: options.limit || 10 });
      const documents = await this.mockSearchDocuments(query, { limit: options.limit || 10 });
      const procedures = await this.mockSearchProcedures(query, { limit: options.limit || 10 });

      return {
        success: true,
        results: [patients, documents, procedures],
      };
    }
  }

  // Indexing Methods (for data synchronization)
  async indexPatient(patient: Patient) {
    if (!this.isAvailable) return { success: true }; // Mock success

    try {
      const algoliaPatient = {
        objectID: patient.id,
        name: patient.name,
        firstName: patient.firstName,
        lastName: patient.lastName,
        mrn: patient.mrn,
        age: patient.age,
        specialty: patient.specialty || "Primary Care",
        status: patient.status,
        chiefComplaint: patient.chiefComplaint,
        lastVisit: patient.lastVisit,
        email: patient.email,
        phone: patient.phone,
        // Enhanced searchable fields
        medications: patient.medications?.map((m: any) => m.name) || [],
        allergies: patient.allergies || [],
        problems: patient.conditions || patient.primaryConditions || [],
        // Searchable text concatenation
        _searchableText: [
          patient.name,
          patient.mrn,
          patient.email,
          patient.chiefComplaint,
          patient.specialty,
          ...(patient.medications?.map((m: any) => m.name) || []),
          ...(patient.allergies || []),
          ...(patient.conditions || []),
        ]
          .filter(Boolean)
          .join(" "),
        _tags: [patient.status, patient.gender, patient.specialty].filter(Boolean),
      };

      await this.client.saveObject({
        indexName: this.patientsIndex,
        body: algoliaPatient,
      });
      return { success: true };
    } catch (error: unknown) {
      return { success: false, error: error instanceof Error ? error.message : "Unknown error" };
    }
  }

  async indexDocument(document: {
    id: string;
    title: string;
    type: string;
    patientId: string;
    patientName: string;
    content: string;
    summary: string;
    timestamp: string;
    provider: string;
  }) {
    if (!this.isAvailable) return { success: true }; // Mock success

    try {
      const algoliaDocument = {
        objectID: document.id,
        title: document.title,
        type: document.type,
        patientId: document.patientId,
        patientName: document.patientName,
        content: document.content.substring(0, 8000), // Limit content size
        summary: document.summary,
        timestamp: document.timestamp,
        provider: document.provider,
        _searchableText: [
          document.title,
          document.summary,
          document.patientName,
          document.content.substring(0, 2000),
        ].join(" "),
      };

      await this.client.saveObject({
        indexName: this.documentsIndex,
        body: algoliaDocument,
      });
      return { success: true };
    } catch (error: unknown) {
      return { success: false, error: error instanceof Error ? error.message : "Unknown error" };
    }
  }

  async bulkIndexPatients(patients: Patient[]) {
    if (!this.isAvailable) return { success: true, indexed: patients.length };

    try {
      const algoliaPatients = patients.map((patient) => ({
        objectID: patient.id,
        name: patient.name,
        firstName: patient.firstName,
        lastName: patient.lastName,
        mrn: patient.mrn,
        age: patient.age,
        specialty: patient.specialty || "Primary Care",
        status: patient.status,
        chiefComplaint: patient.chiefComplaint,
        lastVisit: patient.lastVisit,
        email: patient.email,
        phone: patient.phone,
        _searchableText: [patient.name, patient.mrn, patient.email, patient.chiefComplaint, patient.specialty]
          .filter(Boolean)
          .join(" "),
      }));

      await this.client.saveObjects({
        indexName: this.patientsIndex,
        objects: algoliaPatients,
      });
      return { success: true, indexed: algoliaPatients.length };
    } catch (error: unknown) {
      return { success: false, error: error instanceof Error ? error.message : "Unknown error" };
    }
  }

  // Autocomplete suggestions
  async getPatientSuggestions(query: string, limit = 5) {
    if (!this.isAvailable) {
      return this.mockGetPatientSuggestions(query, limit);
    }

    try {
      const results = await this.client.searchSingleIndex({
        indexName: this.patientsIndex,
        searchParams: {
          query,
          hitsPerPage: limit,
          attributesToRetrieve: ["objectID", "name", "mrn", "specialty"],
          attributesToHighlight: ["name", "mrn"],
        },
      });

      return {
        success: true,
        suggestions: results.hits.map((hit: any) => ({
          id: hit.objectID,
          name: hit.name,
          mrn: hit.mrn,
          specialty: hit.specialty,
          highlight: hit._highlightResult,
        })),
        error: undefined,
      };
    } catch (_error: unknown) {
      return this.mockGetPatientSuggestions(query, limit);
    }
  }

  // Mock implementations for when Algolia is not available
  private mockSearchPatients(query: string, _options: any) {
    const mockResults = [
      {
        objectID: "patient_1",
        name: "John Smith",
        mrn: "MRN0001234",
        age: 45,
        specialty: "Oncology",
        status: "Active",
        chiefComplaint: "Follow-up for lung cancer treatment",
        lastVisit: "2025-09-10",
      },
      {
        objectID: "patient_2",
        name: "Sarah Johnson",
        mrn: "MRN0001235",
        age: 38,
        specialty: "Gastroenterology",
        status: "Active",
        chiefComplaint: "IBD management",
        lastVisit: "2025-09-11",
      },
    ].filter(
      (p) =>
        p.name.toLowerCase().includes(query.toLowerCase()) ||
        p.mrn.toLowerCase().includes(query.toLowerCase()) ||
        p.chiefComplaint.toLowerCase().includes(query.toLowerCase())
    );

    return Promise.resolve({
      success: true,
      data: mockResults,
      total: mockResults.length,
      page: 0,
      pages: 1,
      error: undefined,
    });
  }

  private mockSearchDocuments(_query: string, _options: any) {
    const mockResults = [
      {
        objectID: "doc_1",
        title: "Radiology Report - Chest CT",
        type: "Radiology",
        patientName: "John Smith",
        timestamp: "2025-09-10T10:00:00Z",
        summary: "No new lesions identified. Stable appearance.",
        provider: "Dr. Wilson",
      },
      {
        objectID: "doc_2",
        title: "Lab Results - CBC with Differential",
        type: "Laboratory",
        patientName: "Sarah Johnson",
        timestamp: "2025-09-11T08:30:00Z",
        summary: "WBC count within normal limits. Hemoglobin stable.",
        provider: "Dr. Chen",
      },
    ];

    return Promise.resolve({
      success: true,
      data: mockResults,
      total: mockResults.length,
      page: 0,
      pages: 1,
      error: undefined,
    });
  }

  private mockSearchProcedures(_query: string, _options: any) {
    const mockResults = [
      {
        objectID: "proc_1",
        name: "Colonoscopy",
        cptCode: "45378",
        specialty: "Gastroenterology",
        patientName: "Sarah Johnson",
        date: "2025-09-11",
        provider: "Dr. Patel",
        status: "Completed",
      },
    ];

    return Promise.resolve({
      success: true,
      data: mockResults,
      total: mockResults.length,
      page: 0,
      pages: 1,
      error: undefined,
    });
  }

  private mockGetPatientSuggestions(query: string, limit: number) {
    const mockSuggestions = [
      { id: "patient_1", name: "John Smith", mrn: "MRN0001234", specialty: "Oncology" },
      { id: "patient_2", name: "Sarah Johnson", mrn: "MRN0001235", specialty: "Gastroenterology" },
    ]
      .filter(
        (p) => p.name.toLowerCase().includes(query.toLowerCase()) || p.mrn.toLowerCase().includes(query.toLowerCase())
      )
      .slice(0, limit);

    return Promise.resolve({
      success: true,
      suggestions: mockSuggestions,
      error: undefined,
    });
  }
}

// Singleton instance
export const algoliaSearchService = new AlgoliaSearchService();
